import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getDashboard } from '../../services/dashboardService'
import { useOrderActions, type OrderPatch } from '../../hooks/useOrderActions'
import type { DealerDashboard, DealerOrderItem } from '../../types'
import { formatDuration, formatPercent, formatRelative, formatVehicle, isCopiedNotSent, lastDaysRange } from '../../utils/format'
import {
  CopiedHint, IssuePills, ReportLinkActions, SendAction,
} from '../../components/Orders/OrderParts'
import OrderDetailPanel from '../../components/Orders/OrderDetailPanel'
import PhoneEditor from '../../components/Orders/PhoneEditor'
import { ChevronRightIcon } from '../../components/Orders/icons'
import DayChart, { SERIES } from './DayChart'
import './DashboardPage.css'

const PERIODS = [7, 30, 90] as const
type PeriodDays = typeof PERIODS[number]
const DEFAULT_PERIOD: PeriodDays = 30

export default function DashboardPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const [searchParams, setSearchParams] = useSearchParams()

  const periodParam = Number(searchParams.get('period'))
  const period: PeriodDays = (PERIODS as readonly number[]).includes(periodParam) ? (periodParam as PeriodDays) : DEFAULT_PERIOD

  const [data, setData] = useState<DealerDashboard | null>(null)
  const [awaiting, setAwaiting] = useState<DealerOrderItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const { from, to } = lastDaysRange(period)
    setLoading(true)
    setError(false)
    getDashboard(from, to)
      .then((res) => {
        if (cancelled) return
        setData(res)
        setAwaiting(res.awaitingSend ?? [])
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[ERROR] Failed to load dealer dashboard', err)
        setError(true)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [period, reloadKey])

  const patchItem: OrderPatch = useCallback((id, patch) => {
    setAwaiting((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)))
  }, [])
  const actions = useOrderActions(patchItem)
  const selected = useMemo(() => awaiting.find((it) => it.id === selectedId) ?? null, [awaiting, selectedId])
  const closePanel = useCallback(() => setSelectedId(null), [])

  // отмеченные на этом экране уже не ждут отправки — поправляем плитки без перезагрузки
  const sentHere = awaiting.filter((it) => it.deliveryStatus !== 'NOT_SENT').length
  const totals = data?.totals
  const awaitingCount = totals ? Math.max(0, totals.awaitingSend - sentHere) : 0
  const sentCount = totals ? totals.sent + sentHere : 0

  function setPeriod(p: PeriodDays) {
    setSearchParams(p === DEFAULT_PERIOD ? {} : { period: String(p) }, { replace: true })
  }

  return (
    <div className="dealer-dashboard">
      <div className="dealer-page__header dealer-dashboard__header">
        <div>
          <h1 className="dealer-page__title">{t('dealer.dashboard.title')}</h1>
          <p className="dealer-page__subtitle">{t('dealer.dashboard.subtitle')}</p>
        </div>
        <div className="dealer-periods" role="group" aria-label={t('dealer.dashboard.period_label')}>
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              className={`dealer-period${period === p ? ' dealer-period--active' : ''}`}
              aria-pressed={period === p}
              onClick={() => setPeriod(p)}
            >
              {t('dealer.dashboard.period_days', { count: p })}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="dealer-card dealer-empty">
          <p>{t('dealer.dashboard.load_failed')}</p>
          <button type="button" className="dealer-btn dealer-btn--secondary dealer-btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
            {t('dealer.common.retry')}
          </button>
        </div>
      ) : !data || !totals ? (
        <div className="dealer-card dealer-empty"><div className="dealer-spinner dealer-dashboard__spinner" /></div>
      ) : (
        <div className={`dealer-dashboard__grid${loading ? ' dealer-dashboard__grid--loading' : ''}`}>
          {/* KPI */}
          <div className="dealer-kpis">
            <div className="dealer-kpi">
              <span className="dealer-kpi__label">{t('dealer.dashboard.kpi_inspections')}</span>
              <span className="dealer-kpi__value">{totals.inspections}</span>
              <span className="dealer-kpi__sub">{t('dealer.dashboard.kpi_ready', { count: totals.ready })}</span>
            </div>
            <Link
              to="/requests?delivery=NOT_SENT"
              className={`dealer-kpi dealer-kpi--link${awaitingCount > 0 ? ' dealer-kpi--warn' : ''}`}
            >
              <span className="dealer-kpi__label">{t('dealer.dashboard.kpi_awaiting')}</span>
              <span className="dealer-kpi__value">{awaitingCount}</span>
              <span className="dealer-kpi__sub dealer-kpi__more">
                {t('dealer.dashboard.kpi_awaiting_link')} <ChevronRightIcon />
              </span>
            </Link>
            <div className="dealer-kpi">
              <span className="dealer-kpi__label">{t('dealer.dashboard.kpi_sent')}</span>
              <span className="dealer-kpi__value">{sentCount}</span>
              <span className="dealer-kpi__sub">{t('dealer.dashboard.kpi_sent_sub')}</span>
            </div>
            <div className="dealer-kpi">
              <span className="dealer-kpi__label">{t('dealer.dashboard.kpi_viewed')}</span>
              <span className="dealer-kpi__value">{totals.viewed}</span>
              <span className="dealer-kpi__sub">
                {t('dealer.dashboard.kpi_view_rate', { rate: formatPercent(totals.viewRate, lang) })}
              </span>
            </div>
          </div>

          {/* К отправке */}
          <section className="dealer-card dealer-dashboard__card dealer-needs">
            <div className="dealer-dashboard__card-head">
              <h2 className="dealer-dashboard__card-title">
                {t('dealer.dashboard.needs_title')}
                {awaitingCount > 0 && <span className="dealer-tab__count dealer-tab__count--hot">{awaitingCount}</span>}
              </h2>
              <Link to="/requests?delivery=NOT_SENT" className="dealer-link dealer-dashboard__more">
                {t('dealer.dashboard.needs_view_all')}
              </Link>
            </div>
            {awaiting.length === 0 ? (
              <p className="dealer-dashboard__empty">{t('dealer.dashboard.needs_empty')}</p>
            ) : (
              <ul className="dealer-needs__list">
                {awaiting.map((item) => (
                  <li
                    key={item.id}
                    className={`dealer-needs__item${isCopiedNotSent(item) ? ' dealer-needs__item--attention' : ''}${item.deliveryStatus !== 'NOT_SENT' ? ' dealer-needs__item--done' : ''}`}
                    onClick={() => setSelectedId(item.id)}
                    onKeyDown={(e) => {
                      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        setSelectedId(item.id)
                      }
                    }}
                    tabIndex={0}
                  >
                    <div className="dealer-needs__info">
                      <span className="dealer-table__primary">
                        #{item.orderNumber} · {formatVehicle(item.carYear, item.carModel) || '—'}
                      </span>
                      <span className="dealer-table__secondary">
                        {formatRelative(item.createdAt, lang)}
                        {item.mechanicName ? ` · ${item.mechanicName}` : ''}
                      </span>
                      <span className="dealer-needs__meta">
                        <IssuePills item={item} />
                        <CopiedHint item={item} />
                      </span>
                    </div>
                    <div className="dealer-needs__copy">
                      <PhoneEditor item={item} actions={actions} compact />
                      <ReportLinkActions item={item} actions={actions} />
                    </div>
                    <div className="dealer-needs__action">
                      <SendAction item={item} actions={actions} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Динамика по дням */}
          <section className="dealer-card dealer-dashboard__card dealer-dashboard__chart">
            <div className="dealer-dashboard__card-head">
              <h2 className="dealer-dashboard__card-title">{t('dealer.dashboard.chart_title')}</h2>
              <div className="dealer-legend">
                {SERIES.map((s) => (
                  <span key={s.key} className="dealer-legend__item">
                    <span className="dealer-chart__swatch" style={{ background: s.color }} />
                    {t(`dealer.dashboard.series_${s.key}`)}
                  </span>
                ))}
              </div>
            </div>
            {totals.inspections === 0 && totals.sent === 0 && totals.viewed === 0 ? (
              <p className="dealer-dashboard__empty">{t('dealer.dashboard.no_data')}</p>
            ) : (
              <DayChart days={data.byDay} />
            )}
          </section>

          {/* Скорость */}
          <section className="dealer-card dealer-dashboard__card">
            <h2 className="dealer-dashboard__card-title">{t('dealer.dashboard.times_title')}</h2>
            <div className="dealer-times">
              <div className="dealer-times__item">
                <span className="dealer-times__value">{formatDuration(data.avgCreatedToSentMinutes, t)}</span>
                <span className="dealer-times__label">{t('dealer.dashboard.time_created_to_sent')}</span>
              </div>
              <div className="dealer-times__item">
                <span className="dealer-times__value">{formatDuration(data.avgSentToViewedMinutes, t)}</span>
                <span className="dealer-times__label">{t('dealer.dashboard.time_sent_to_viewed')}</span>
              </div>
            </div>
          </section>

          {/* Найденные проблемы */}
          <section className="dealer-card dealer-dashboard__card">
            <h2 className="dealer-dashboard__card-title">{t('dealer.dashboard.issues_title')}</h2>
            {totals.inspections === 0 ? (
              <p className="dealer-dashboard__empty">{t('dealer.dashboard.no_data')}</p>
            ) : (
              <div className="dealer-shares">
                <ShareBar
                  label={t('dealer.dashboard.issues_critical')}
                  value={totals.withCritical}
                  total={totals.inspections}
                  tone="critical"
                />
                <ShareBar
                  label={t('dealer.dashboard.issues_minor')}
                  value={totals.withMinor}
                  total={totals.inspections}
                  tone="minor"
                />
              </div>
            )}
          </section>

          {/* Механики */}
          <section className="dealer-card dealer-dashboard__card dealer-dashboard__mechanics">
            <h2 className="dealer-dashboard__card-title">{t('dealer.dashboard.mechanics_title')}</h2>
            {data.byMechanic.length === 0 ? (
              <p className="dealer-dashboard__empty">{t('dealer.dashboard.no_data')}</p>
            ) : (
              <table className="dealer-mini-table">
                <thead>
                  <tr>
                    <th>{t('dealer.dashboard.col_mechanic')}</th>
                    <th className="dealer-num">{t('dealer.dashboard.col_inspections')}</th>
                    <th className="dealer-num">{t('dealer.dashboard.col_with_issues')}</th>
                    <th className="dealer-num">{t('dealer.dashboard.col_issue_share')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.byMechanic].sort((a, b) => b.inspections - a.inspections).map((m) => (
                    <tr key={m.mechanicId}>
                      <td>{m.name || '—'}</td>
                      <td className="dealer-num">{m.inspections}</td>
                      <td className="dealer-num">{m.withIssues}</td>
                      <td className="dealer-num">{formatPercent(m.inspections ? m.withIssues / m.inspections : null, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      )}

      {selected && <OrderDetailPanel item={selected} actions={actions} onClose={closePanel} />}
    </div>
  )
}

function ShareBar({ label, value, total, tone }: { label: string; value: number; total: number; tone: 'critical' | 'minor' }) {
  const { t, i18n } = useTranslation()
  const share = total ? value / total : 0
  return (
    <div className="dealer-share">
      <div className="dealer-share__head">
        <span>{label}</span>
        <span className="dealer-share__value">
          {formatPercent(share, i18n.language)}
          <span className="dealer-muted"> · {t('dealer.dashboard.of_inspections', { value, total })}</span>
        </span>
      </div>
      <div className="dealer-share__track">
        <div className={`dealer-share__bar dealer-share__bar--${tone}`} style={{ width: `${Math.round(share * 100)}%` }} />
      </div>
    </div>
  )
}
